import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { BRAND, SERVICE_OPTIONS, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  location: "",
  service: "",
  project_type: "",
  current_software: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please complete name, email, service and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        overline="— Contact"
        title="Let's review your"
        accent="design, visualization or production requirements."
        body="Tell us what you are designing, presenting or manufacturing. We will identify whether the project requires interior design, architectural visualization, Cabinet Vision development, UCS automation, production drafting, reporting, CNC workflow support, training or a complete design-to-production system."
        image={IMAGES.textureMood}
        testid="contact-hero"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-16" data-testid="contact-section">
        <Reveal className="md:col-span-4 space-y-10">
          <div>
            <div className="overline mb-3">Direct</div>
            <a data-testid="contact-direct-email" href={`mailto:${BRAND.email}`} className="flex items-start gap-3 group">
              <Mail size={18} className="text-[#B58B5D] mt-1" />
              <span className="font-serif text-2xl text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors">{BRAND.email}</span>
            </a>
          </div>
          <div>
            <div className="overline mb-3">Location</div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-[#B58B5D] mt-1" />
              <div className="text-[#EAEAEA]">
                Remote worldwide
              </div>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Confidentiality</div>
            <p className="text-sm text-[#A3A3A3] leading-relaxed">
              Please do not transmit confidential databases, proprietary company standards or
              customer files until confidentiality and file-transfer arrangements are in place.
            </p>
          </div>
        </Reveal>

        <Reveal className="md:col-span-8" delay={120}>
          {done ? (
            <div data-testid="contact-success" className="dl-card p-12 text-center">
              <div className="overline mb-4">— Sent</div>
              <h2 className="font-serif text-4xl mb-4">Thank you.</h2>
              <p className="text-[#A3A3A3]">We will respond shortly with next steps.</p>
              <button onClick={() => setDone(false)} className="dl-btn mt-8">Send another</button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <label className="block">
                  <span className="overline">Name *</span>
                  <input data-testid="contact-name" required name="name" value={form.name} onChange={onChange} className="dl-input" placeholder="Your full name" />
                </label>
                <label className="block">
                  <span className="overline">Company</span>
                  <input data-testid="contact-company" name="company" value={form.company} onChange={onChange} className="dl-input" placeholder="Company or studio" />
                </label>
                <label className="block">
                  <span className="overline">Email *</span>
                  <input data-testid="contact-email" required type="email" name="email" value={form.email} onChange={onChange} className="dl-input" placeholder="you@example.com" />
                </label>
                <label className="block">
                  <span className="overline">Phone</span>
                  <input data-testid="contact-phone" name="phone" value={form.phone} onChange={onChange} className="dl-input" placeholder="+1" />
                </label>
                <label className="block">
                  <span className="overline">Location</span>
                  <input data-testid="contact-location" name="location" value={form.location} onChange={onChange} className="dl-input" placeholder="City, country" />
                </label>
                <label className="block">
                  <span className="overline">Service required *</span>
                  <select data-testid="contact-service" required name="service" value={form.service} onChange={onChange} className="dl-input">
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">Project type</span>
                  <input data-testid="contact-project-type" name="project_type" value={form.project_type} onChange={onChange} className="dl-input" placeholder="Residential / Commercial / Hospitality" />
                </label>
                <label className="block">
                  <span className="overline">Current software</span>
                  <input data-testid="contact-software" name="current_software" value={form.current_software} onChange={onChange} className="dl-input" placeholder="Cabinet Vision version, SketchUp, AutoCAD…" />
                </label>
              </div>
              <label className="block">
                <span className="overline">Project details *</span>
                <textarea data-testid="contact-message" required name="message" value={form.message} onChange={onChange} className="dl-input" placeholder="Describe the project, current workflow or problem you would like to solve." />
              </label>
              <div className="flex justify-end pt-4">
                <button data-testid="contact-submit" type="submit" disabled={submitting} className="dl-btn-primary disabled:opacity-50">
                  {submitting ? "Sending…" : "Send message"} <ArrowUpRight size={14} />
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
